import React from 'react';
import { SelectionNewVersion } from 'src/domains/sportsbook/webview/components/Selection';
import { ExpandButton } from 'src/domains/sportsbook/webview/components/expandButton/ExpandButton';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { MarketHeader } from 'src/domains/sportsbook/webview/components/marketHeader/MarketHeader';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import { observer } from 'src/utils/mobx-react';
import { MarketId, SelectionId } from 'src_common/common/websocket2/id/WebsocketId';
import { EventMarketItemType } from 'src_common/common/websocket2/modelsApi/EventMarkets';
import { LockIcon } from 'src/ui/assets/icons/other/LockIcon';
import { SelectionsGroupWrap } from 'src/domains/sportsbook/webview/components/selectionsGroupUnified/SelectionGroupUnified.style';
import {
    ComboOverUnderRoot,
    HeaderCell,
    HeaderWrapper,
    LineCell,
    MarketRows,
    SelectionCell,
} from './ComboOverUnderLineMarketGroup.style';
import { buildComboOverUnderColumns, canStackComboOverUnder, ComboOverUnderColumn } from './comboOverUnderStack';

interface PropsType {
    name: string;
    markets: Array<MarketId>;
    marketItems: Array<EventMarketItemType>;
    selections: Record<number | string, SelectionModel[]>;
    isOpen: boolean;
    onClick(): void;
    marketId: MarketId;
    suspendedMarketIds?: ReadonlySet<number>;
}

const INITIAL_ROWS_LIMIT = 4;

interface ComboRowType {
    key: string;
    marketOldId: number;
    line: number | null;
    columns: Array<ComboOverUnderColumn<SelectionId>>;
    forceSuspended: boolean;
}

const resolveMarketSelections = (
    marketId: MarketId,
    marketModel: MarketModel,
    selections: Record<number | string, SelectionModel[]>
): SelectionModel[] => {
    const fromProps = selections[marketModel.id];
    if (fromProps !== undefined && fromProps.length > 0) {
        return fromProps;
    }

    const fromRawId = selections[marketId.toOldId()];
    if (fromRawId !== undefined && fromRawId.length > 0) {
        return fromRawId;
    }

    return marketModel.selections.filter((selection) => selection.display);
};

const toComboInputs = (marketSelections: SelectionModel[]) => {
    return marketSelections.map((selection) => ({
        name: selection.name,
        templateId: selection.templateId,
        identifier: selection.identifier,
        selectionId: selection.id2,
    }));
};

const lineLabel = (line: number | null): string => {
    if (line === null) {
        return '-';
    }
    return Number.isInteger(line) ? `${line}.0` : `${line}`;
};

const getMarketRows = (
    groupName: string,
    markets: Array<MarketId>,
    marketItems: Array<EventMarketItemType>,
    selections: Record<number | string, SelectionModel[]>,
    suspendedMarketIds: ReadonlySet<number> | undefined,
    hideSuspendedLines: boolean
): ComboRowType[] => {
    const itemsById = new Map(marketItems.map((item) => [item.id2.toOldId(), item]));

    return markets
        .map((marketId, index): ComboRowType | null => {
            const marketOldId = marketId.toOldId();
            const item = itemsById.get(marketOldId);
            const forceSuspended = suspendedMarketIds?.has(marketOldId) === true;
            const marketModel = marketId.getModel();

            if (marketModel === null) {
                return {
                    key: `${groupName}-slot-${index}`,
                    marketOldId,
                    line: item?.keyLine ?? null,
                    columns: [],
                    forceSuspended: true,
                };
            }

            if (hideSuspendedLines === true && forceSuspended === false && marketModel.activeAndDisplayed === false) {
                return null;
            }

            const marketSelections = resolveMarketSelections(marketId, marketModel, selections);
            const columns = buildComboOverUnderColumns(toComboInputs(marketSelections)) ?? [];

            return {
                key: `${groupName}-slot-${index}`,
                marketOldId,
                line: marketModel.line ?? item?.keyLine ?? null,
                columns,
                forceSuspended,
            };
        })
        .filter((row): row is ComboRowType => row !== null)
        .sort((a, b) => {
            if (a.line === null && b.line === null) {
                return 0;
            }
            if (a.line === null) {
                return 1;
            }
            if (b.line === null) {
                return -1;
            }
            return a.line - b.line;
        })
        .map((row, index) => ({
            ...row,
            key: `${groupName}-slot-${index}`,
        }));
};

const SuspendedPlaceholder = (): JSX.Element => <LockIcon className='selection-suspended--icon' />;

const StackedOuCell = (props: {
    overSelectionId: SelectionId | undefined;
    underSelectionId: SelectionId | undefined;
    forceSuspended: boolean;
}): JSX.Element => (
    <SelectionCell>
        {props.overSelectionId !== undefined ? (
            <SelectionNewVersion
                selectionId={props.overSelectionId}
                sp={false}
                lineLabel='Over'
                forceSuspended={props.forceSuspended}
            />
        ) : props.forceSuspended ? (
            <SuspendedPlaceholder />
        ) : null}
        {props.underSelectionId !== undefined ? (
            <SelectionNewVersion
                selectionId={props.underSelectionId}
                sp={false}
                lineLabel='Under'
                forceSuspended={props.forceSuspended}
            />
        ) : props.forceSuspended ? (
            <SuspendedPlaceholder />
        ) : null}
    </SelectionCell>
);

export const ComboOverUnderLineMarketGroup = observer('ComboOverUnderLineMarketGroup', (props: PropsType) => {
    const { name, markets, marketItems, selections, isOpen, onClick, marketId, suspendedMarketIds } = props;

    return (
        <ComboOverUnderRoot
            className='market-group--combo-over-under-line'
            data-test='combo-over-under-line-market-group'
        >
            <MarketHeader
                name={name}
                isOpen={isOpen}
                onClick={onClick}
                marketId={marketId}
                keepVisibleWhileLoading={true}
            />
            {isOpen ? (
                <MarketBody
                    groupName={name}
                    markets={markets}
                    marketItems={marketItems}
                    selections={selections}
                    suspendedMarketIds={suspendedMarketIds}
                />
            ) : null}
        </ComboOverUnderRoot>
    );
});

const MarketBody = observer(
    'ComboOverUnderLineMarketBody',
    (props: {
        groupName: string;
        markets: Array<MarketId>;
        marketItems: Array<EventMarketItemType>;
        selections: Record<number | string, SelectionModel[]>;
        suspendedMarketIds?: ReadonlySet<number>;
    }) => {
        const [isExpanded, setIsExpanded] = React.useState(false);
        const stableHeadersRef = React.useRef<string[]>([]);

        const { groupName, markets, marketItems, selections, suspendedMarketIds } = props;

        const firstMarketModel = markets.map((marketId) => marketId.getModel()).find((model) => model !== null) ?? null;
        const isInPlay = firstMarketModel?.eventModel?.timeSettingsStarted === true;
        const hideSuspendedLines = isInPlay === false;

        const rows = getMarketRows(groupName, markets, marketItems, selections, suspendedMarketIds, hideSuspendedLines);

        if (markets.length === 0 || rows.length === 0) {
            return null;
        }

        const headerRow = rows.find((row) => row.columns.length > 0);
        const nextHeaders = headerRow?.columns.map((column) => column.header) ?? [];
        if (nextHeaders.length > 0) {
            stableHeadersRef.current = nextHeaders;
        }
        const headers = nextHeaders.length > 0 ? nextHeaders : stableHeadersRef.current;

        const visibleRows = isExpanded === true ? rows : rows.slice(0, INITIAL_ROWS_LIMIT);

        const toggleExpand = (e: React.MouseEvent): void => {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
        };

        return (
            <>
                {headers.length > 0 ? (
                    <HeaderWrapper>
                        <HeaderCell>
                            <I18n
                                langKey='events.total-goals-market.goals'
                                defaultText='Goals'
                            />
                        </HeaderCell>
                        {headers.map((header) => (
                            <HeaderCell key={header}>{header}</HeaderCell>
                        ))}
                    </HeaderWrapper>
                ) : null}
                <SelectionsGroupWrap>
                    <MarketRows>
                        {visibleRows.map((row) => (
                            <React.Fragment key={row.key}>
                                <LineCell>{lineLabel(row.line)}</LineCell>
                                {headers.map((header, columnIndex) => {
                                    const column =
                                        row.columns.find((item) => item.header === header) ?? row.columns[columnIndex];

                                    return (
                                        <StackedOuCell
                                            key={`${row.key}-${header}`}
                                            overSelectionId={column?.overSelectionId}
                                            underSelectionId={column?.underSelectionId}
                                            forceSuspended={row.forceSuspended}
                                        />
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </MarketRows>
                    {rows.length > INITIAL_ROWS_LIMIT ? (
                        <ExpandButton
                            isExpanded={isExpanded}
                            toggleExpand={toggleExpand}
                        />
                    ) : null}
                </SelectionsGroupWrap>
            </>
        );
    }
);

export const shouldRenderComboOverUnderStack = (
    markets: Array<MarketId>,
    selections: Record<number | string, SelectionModel[]>
): boolean => {
    for (const marketId of markets) {
        const marketModel = marketId.getModel();
        if (marketModel === null) {
            continue;
        }

        const marketSelections = resolveMarketSelections(marketId, marketModel, selections);
        if (marketSelections.length === 0) {
            continue;
        }
        return canStackComboOverUnder(toComboInputs(marketSelections));
    }
    return false;
};
